import { useState, useCallback, useEffect, useRef } from "preact/hooks";
import { animationHandler } from "@utils/scroll-snap/animationHandler";
import { getNextScrollPosition } from "@utils/scroll-snap/getNextScrollPosition";
import { useCheckScrollSpeedX } from "./useCheckScrollSpeed";
import { animate } from "@utils/scroll-snap/animate";

export function useScrollEvent({
  element,
  timeout,
  threshold,
}: {
  element?: HTMLElement | null;
  deltaX?: number;
  timeout: number;
  threshold: number;
}) {
  const [totalItems, setTotalItems] = useState<number | null>(null);
  const [animationFrameTimer, setAnimationFrameTimer] = useState<
    number | undefined
  >();
  const [scrollHandlerTimer, setScrollHandlerTimer] = useState<
    NodeJS.Timeout | undefined
  >();
  const [scrollStartX, setScrollStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentItem, setCurrentItem] = useState(0);
  const cItem = useRef(0);
  const [scrollToX, setScrollToX] = useState(0);
  const deltaX = useCheckScrollSpeedX({
    scrollLeft,
  });

  const handleSetAnimationFrame = (animationFrameTimer: number | undefined) => {
    setAnimationFrameTimer(animationFrameTimer);
  };

  const handleSetStartX = (startX: number) => {
    setScrollStartX(startX);
  };

  const handleSetCurrentItem = useCallback(
    (index: number) => {
      if (currentItem !== index && element && totalItems) {
        setCurrentItem(index);
        cItem.current = index;
        const snapLengthX = Math.round(element.scrollWidth / totalItems);
        setScrollToX(index * snapLengthX);
      }
    },
    [currentItem, element, totalItems],
  );

  const handleClickCurrentItem = useCallback(
    (index: number) => {
      setCurrentItem(index);
      if (element && totalItems) {
        const snapLengthX = Math.round(element.scrollWidth / totalItems);
        setScrollToX(index * snapLengthX);
        if (scrollHandlerTimer) {
          clearTimeout(scrollHandlerTimer);
        }

        animate({
          obj: element,
          endX: index * snapLengthX,
          startX: scrollStartX,
          handleSetAnimationFrame,
          handleSetStartX,
        });
      }
    },
    [scrollToX, element, totalItems],
  );

  const handleSetPrevItem = useCallback(() => {
    const prev = currentItem - 1;
    if (prev >= 0) {
      setCurrentItem(prev);
      if (element && totalItems) {
        const snapLengthX = Math.round(element.scrollWidth / totalItems);
        setScrollToX(prev * snapLengthX);
        if (scrollHandlerTimer) {
          clearTimeout(scrollHandlerTimer);
        }

        animate({
          obj: element,
          endX: prev * snapLengthX,
          startX: scrollStartX,
          handleSetAnimationFrame,
          handleSetStartX,
        });
      }
    }
  }, [scrollToX, element, totalItems]);

  const handleSetNextItem = useCallback(() => {
    if (totalItems) {
      const next = currentItem + 1;
      if (next <= totalItems - 1) {
        setCurrentItem(next);
        if (element && totalItems) {
          const snapLengthX = Math.round(element.scrollWidth / totalItems);
          setScrollToX(next * snapLengthX);
          if (scrollHandlerTimer) {
            clearTimeout(scrollHandlerTimer);
          }

          animate({
            obj: element,
            endX: next * snapLengthX,
            startX: scrollStartX,
            handleSetAnimationFrame,
            handleSetStartX,
          });
        }
      }
    }
  }, [currentItem, totalItems]);

  useEffect(() => {
    if (element && !totalItems) {
      setTotalItems(element?.childNodes.length);
    }
  }, [element, totalItems]);

  const handleScroll = useCallback(() => {
    if (animationFrameTimer) {
      clearTimeout(animationFrameTimer);
    }

    if (element && totalItems) {
      // if a previous timeout exists, clear it.
      if (scrollHandlerTimer) {
        // we only want to call a timeout once after scrolling..
        clearTimeout(scrollHandlerTimer);
      }

      setScrollLeft(element.scrollLeft);
      const directionX = Math.sign(deltaX);

      const snapLengthX = Math.round(element.scrollWidth / totalItems);
      getNextScrollPosition({
        target: element,
        directionX,
        threshold,
        snapLengthX,
        handleSetCurrentItem,
        currentItem,
      });
      // const scrollToX = currentItem * snapLengthX;
      const scrollTimer = setTimeout(
        animationHandler({
          scrollStartX,
          target: element,
          handleSetAnimationFrame,
          handleSetStartX,
          scrollToX,
        }),
        timeout,
      );
      setScrollHandlerTimer(scrollTimer);
    }
  }, [
    animationFrameTimer,
    element,
    totalItems,
    scrollHandlerTimer,
    deltaX,
    threshold,
    handleSetCurrentItem,
    scrollStartX,
    scrollToX,
    timeout,
  ]);

  useEffect(() => {
    if (element) {
      element.addEventListener("scroll", handleScroll, false);
      return () => element.removeEventListener("scroll", handleScroll, false);
    }
  }, [element, handleScroll]);

  return {
    currentItem,
    handleClickCurrentItem,
    handleSetNextItem,
    handleSetPrevItem,
  };
}
